import React, { useCallback, useEffect, useMemo, useState } from "react"

import _ from "lodash"
import { Event, Map as MapboxMap, MapEventOf } from "mapbox-gl"
import ReactMapbox, { MapRef, ScaleControl } from "react-map-gl/mapbox"

import { useGetLocaleFromList } from "@l2r-front/l2r-i18n"
import { EndFlagIcon, PlaceIcon } from "@l2r-front/l2r-icons"
import { useIsMobileDevice, useTheme } from "@l2r-front/l2r-ui"
import { useApplicationStateContext } from "@l2r-front/l2r-utils"

import { POINT_MIN_BBOX_WIDTH } from "../../constants/bboxMinWidth"
import { CAMPAIGNS_LAYER_CLUSTER_COUNT, CATALOG_ITEMS_LAYER_CLUSTER_COUNT } from "../../constants/layers"
import { BASE_MAP_ID } from "../../constants/map"
import { MapLayerSelector } from "../../containers/MapLayerSelector"
import { UserLocationLayer } from "../../containers/UserLocationLayer"
import { useMapContext } from "../../contexts/MapContext"
import { MapStyle } from "../../contexts/MapContext/MapContext.context"

import { LocationButton } from "../LocationButton"
import { ZoomButtons } from "../ZoomButtons"

import * as Styled from "./Map.styled"

export const PLACE_ICON_ID = "place"
export const ENDFLAG_ICON_ID = "endFlag"

const METERS_PER_LNG_DEGREE_AT_EQUATOR = 111320

const MapIconsInfos = [
    {
        id: PLACE_ICON_ID,
        icon: PlaceIcon,
        convertToSDF: true,
    },
    {
        id: ENDFLAG_ICON_ID,
        icon: EndFlagIcon,
        convertToSDF: false,
    },
]

type MapProps = {
    borderRounded?: boolean
    bottomLeftElements?: React.ReactNode | React.ReactNode[]
    bottomRightElements?: React.ReactNode | React.ReactNode[]
    children?: React.ReactNode
    className?: string
    displayZoomButtons?: boolean
    displayRecenterButton?: boolean
    displayMapLayerSelector?: boolean
    error?: boolean
    id?: string
    isLoading: boolean
    logoPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right"
    mapStyle?: MapStyle
    onRecenter?: () => void
    topLeftElements?: React.ReactNode | React.ReactNode[]
    topRightElements?: React.ReactNode | React.ReactNode[]
    translateTopAnchor?: boolean
}

export const MapLocales = ["en", "fr", "es", "ge"]

export const Map = (props: MapProps) => {

    const {
        borderRounded = false,
        bottomLeftElements = [],
        bottomRightElements,
        children,
        className,
        displayZoomButtons = true,
        displayRecenterButton = true,
        displayMapLayerSelector = true,
        error = false,
        id = BASE_MAP_ID,
        isLoading,
        onRecenter,
        topLeftElements,
        topRightElements = [],
        translateTopAnchor,
        ...mapProps
    } = props

    const [mapRef, setMapRef] = useState<any>(null)
    const [mapState, mapDispatch] = useMapContext()
    const { currentBottomSheetHeight } = useApplicationStateContext()
    const { mapBoundingBoxToFit, fitOptions, getCurrentMapStyle, userLocation } = mapState
    const { setMapBoundingBoxToFit, setMapRef: setContextMapRef, resetMapBoundingBox } = mapDispatch
    const theme = useTheme()
    const isMobile = useIsMobileDevice()
    const mapStyle = mapProps?.mapStyle ?? getCurrentMapStyle(id)
    const [mapBoundingBox, setMapBoundingBox] = useState(null)
    const isBaseMap = id === BASE_MAP_ID

    const map = useMemo(() => {
        if (!mapRef) {
            return
        }

        return mapRef.getMap()
    }, [mapRef])

    const currentLang = useGetLocaleFromList(MapLocales, MapLocales[0])

    interface MapIconInfo {
        id: string
        icon: string
        convertToSDF: boolean
    }

    const handleLoad = useCallback((e: Event) => {
        const mapTarget = e.target as MapboxMap
        MapIconsInfos.forEach((infos: MapIconInfo) => {
            if (!mapTarget.hasImage(infos.id)) {
                mapTarget.loadImage(
                    infos.icon,
                    (error: unknown, icon: unknown) => {
                        if (error) {
                            return console.error(error)
                        }

                        if (!mapTarget.hasImage(infos.id)) {
                            mapTarget.addImage(infos.id, icon as HTMLImageElement, {
                                sdf: infos.convertToSDF,
                            })
                        }
                    },
                )
            }
        })
    }, [])

    const onStyleLoaded = (e: MapEventOf<"styledata">) => {
        setMapLanguage(e.target as MapboxMap, currentLang)
        handleLoad(e)
    }

    useEffect(() => {
        if (!map) {
            return
        }
        const loaded = map.loaded()
        if (!loaded) {
            return
        }
        setMapLanguage(map, currentLang)
    }, [currentLang, map])

    const handleMapRef = useCallback((ref: MapRef | null) => {
        setMapRef(ref)
        setContextMapRef(ref, id)

        if (process.env["NODE_ENV"] === "development") {
            (window as any).map = ref
        }
    }, [setContextMapRef, id])

    useEffect(() => {
        return () => {
            if (setContextMapRef && typeof setContextMapRef === 'function') {
                setContextMapRef(null, id)
            }
            if (window) {
                (window as any).map = undefined
            }
        }
    }, [setContextMapRef, id])

    const zoomIn = useCallback(() => {
        map.zoomIn()
    }, [map])

    const zoomOut = useCallback(() => {
        map.zoomOut()
    }, [map])

    useEffect(() => {
        const currentMapBoundingBoxToFit = mapBoundingBoxToFit?.[id]
        if (!(currentMapBoundingBoxToFit && map) || id === BASE_MAP_ID && mapBoundingBox === currentMapBoundingBoxToFit) {
            return
        }
        const frame = requestAnimationFrame(() => {
            setMapBoundingBox(currentMapBoundingBoxToFit)
            map.resize()

            const defaultPadding = 32
            const paddingValue = fitOptions?.padding ?? defaultPadding
            const horizontalPadding = Math.min(paddingValue, map.getCanvas().width / 8)
            const verticalPadding = Math.min(paddingValue, map.getCanvas().height / 8)

            map.fitBounds(
                [
                    [currentMapBoundingBoxToFit.minLng, currentMapBoundingBoxToFit.minLat],
                    [currentMapBoundingBoxToFit.maxLng, currentMapBoundingBoxToFit.maxLat],
                ],
                {
                    ...(fitOptions ?? {}),
                    padding: {
                        top: verticalPadding,
                        bottom: verticalPadding,
                        left: horizontalPadding,
                        right: horizontalPadding,
                    },
                },
            )
            setMapBoundingBoxToFit(null, undefined, id)
        })
        return () => cancelAnimationFrame(frame)
    }, [id, map, mapBoundingBox, mapBoundingBoxToFit, setMapBoundingBoxToFit, fitOptions])

    const handleRecenterOnUserLocation = useCallback((coordinates: GeolocationCoordinates | null = null) => {
        const userCoordinates = coordinates ?? userLocation
        if (!userCoordinates) {
            return
        }

        const accuracyLng = userCoordinates.accuracy / (METERS_PER_LNG_DEGREE_AT_EQUATOR * Math.cos(userCoordinates.latitude * Math.PI / 180))
        const lngOffset = Math.max(accuracyLng, POINT_MIN_BBOX_WIDTH / 2)
        const pointBoundingBox = {
            minLng: userCoordinates.longitude - lngOffset,
            minLat: userCoordinates.latitude,
            maxLng: userCoordinates.longitude + lngOffset,
            maxLat: userCoordinates.latitude,
        }

        if (map) {
            const newCameraWithBounds = map.cameraForBounds([
                [pointBoundingBox?.minLng, pointBoundingBox?.minLat],
                [pointBoundingBox?.maxLng, pointBoundingBox?.maxLat],
            ])
            setMapBoundingBoxToFit(pointBoundingBox, newCameraWithBounds.zoom)
            resetMapBoundingBox()
        }
    }, [
        map,
        resetMapBoundingBox,
        setMapBoundingBoxToFit,
        userLocation,
    ])

    return (
        <Styled.Wrapper className={className} scaleColor={mapStyle.contrastColor} borderRounded={borderRounded}>
            <Styled.Loader color="error" hidden={!error} value={100} variant="determinate" />
            <Styled.Loader color="primary" hidden={!isLoading} />
            <ReactMapbox
                {...mapProps}
                ref={handleMapRef}
                localFontFamily={theme.typography.fontFamily}
                logoPosition={(mapProps)?.logoPosition || (isMobile ? "top-left" : "top-right")}
                mapboxAccessToken={process.env["NX_PUBLIC_MAPBOXTOKEN"]}
                id={id || BASE_MAP_ID}
                mapStyle={mapStyle.mapStyle}
                onLoad={handleLoad}
                onStyleData={onStyleLoaded}
                style={{ width: "100%", height: "100%" }}
            >
                <>
                    <Styled.TopLeftAnchor translateTopAnchor={!!translateTopAnchor}>
                        {topLeftElements}
                    </Styled.TopLeftAnchor>
                    <Styled.BottomLeftAnchor isBaseMap={isBaseMap} translateTopAnchor={!!translateTopAnchor} isMobile={isMobile} currentBottomSheetHeight={currentBottomSheetHeight}>
                        {displayMapLayerSelector ? <MapLayerSelector id="map-style-selector" mapId={id} /> : null}
                        {bottomLeftElements}
                    </Styled.BottomLeftAnchor>
                    <Styled.TopRightAnchor isMobile={isMobile && id === BASE_MAP_ID}>
                        {topRightElements}
                    </Styled.TopRightAnchor>
                    {<Styled.BottomRightAnchor isBaseMap={isBaseMap} translateTopAnchor={translateTopAnchor} isMobile={isMobile} currentBottomSheetHeight={currentBottomSheetHeight}>
                        {bottomRightElements}
                        {displayZoomButtons && !isMobile && <ZoomButtons onZoomIn={zoomIn} onZoomOut={zoomOut} />}
                        {displayRecenterButton && onRecenter && <LocationButton onRecenter={onRecenter} onRecenterOnUserLocation={handleRecenterOnUserLocation} />}
                    </Styled.BottomRightAnchor>}
                </>
                {children}
                <UserLocationLayer />
                <ScaleControl />
            </ReactMapbox >
        </Styled.Wrapper >
    )
}

const EXCLUDED_LAYERS_FOR_TRANSLATION = ["road-number-shield", "road-number", CATALOG_ITEMS_LAYER_CLUSTER_COUNT, CAMPAIGNS_LAYER_CLUSTER_COUNT]

export const setMapLanguage = (map: MapboxMap, lang: string) => {
    const mapStyle = map.getStyle()
    mapStyle.layers.forEach((layer) => {
        if ('layout' in layer && layer.layout && 'text-field' in layer.layout) {
            if (!EXCLUDED_LAYERS_FOR_TRANSLATION.includes(layer.id)) {
                map.setLayoutProperty(layer.id, "text-field", [
                    "coalesce",
                    ["get", "name_" + lang],
                    ["get", "name"],
                ])
            }
        }

    })
}
